import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Container, Row, View } from "@amzn/stencil-react-components/layout";
import { ModalContent, WithModal } from "@amzn/stencil-react-components/modal";
import { Text } from "@amzn/stencil-react-components/text";
import { SelectChildSku, VersionObject } from "common/components/SelectChildSku";
import { getString } from "common/uistringlabels/uiStringUtils";
import NoImageLogo from "img/no_logo_image.png";
import { IItemVersion } from "lib/types/items";
import React, { FC, useState } from "react";
import { useCheckoutFormActions, useCheckoutFormItems } from "view/Page/CheckoutPage/useCheckoutForm";

const PANEL_WIDTH = 367;
const MODAL_WIDTH = 847;

export interface CheckoutItemSidePanelProps {
  options: IItemVersion[];
  checkoutItems: useCheckoutFormItems;
  checkoutActions: useCheckoutFormActions;
  parentItemName: string | undefined;
}

const CheckoutItemSidePanel: FC<CheckoutItemSidePanelProps> = (props: CheckoutItemSidePanelProps) => {
  const checkoutValues = props.checkoutItems.formValues;
  const handleCheckoutChange = props.checkoutActions.handleInputChange;

  const [currentSelected, setCurrentSelected] = useState<VersionObject>(checkoutValues.catalogItemVersion);

  const show = (text: string | null | undefined): string => text ?? "";

  const onConfirm = (closeModal: () => void) => {
    const selectedVersion: IItemVersion | undefined = props.options.find(
      (option) => option.taxonomyId === currentSelected.taxonomyId
    );
    handleCheckoutChange("catalogItemVersion", selectedVersion);
    closeModal();
  };

  const renderChangeVersionModal = ({ close }: { close: () => void }) => {
    return (
      <ModalContent
        maxWidth={null}
        titleText="Change version"
        buttons={[
          <Button key="cancel-modal-button" onClick={() => close()}>
            {getString("checkout.sidePanel.modal.cancelButton")}
          </Button>,
          <Button key="confirm-modal-button" onClick={() => onConfirm(close)}>
            {getString("checkout.sidePanel.modal.saveButton")}
          </Button>,
        ]}
      >
        <Col data-testid="change-version-modal" width={MODAL_WIDTH}>
          <SelectChildSku
            itemChildVersions={props.options}
            selectedVersion={currentSelected}
            onChange={(event) => setCurrentSelected(event)}
          />
        </Col>
      </ModalContent>
    );
  };

  return (
    <Col backgroundColor="neutral05" width={PANEL_WIDTH} data-testid="checkout-side-panel">
      <Container paddingHorizontal={40} paddingTop={32} isFullWidth={true}>
        <Col gridGap="S400" width="100%">
          {/* Shadow */}
          <View
            display="flex"
            height={"110px"}
            width={"110px"}
            padding={"S100"}
            alignItems="flex-start"
            className="img-square-wrapper"
            style={{ borderRadius: "16px" }}
          >
            {/* Logo image */}
            <img
              alt={checkoutValues.catalogItemVersion.displayText}
              src={
                checkoutValues.catalogItemVersion.logoUrl?.trim() !== ""
                  ? checkoutValues.catalogItemVersion.logoUrl
                  : NoImageLogo
              }
              data-testid="checkout-item-img"
              style={{
                borderRadius: "16px",
                width: "100%",
                height: "100%",
                objectFit: "contain",
                alignSelf: "center",
              }}
            />
          </View>
          <Col gridGap="S300">
            <Text color="neutral90" fontSize="T200" fontWeight="bold">
              {`${show(props.parentItemName ?? checkoutValues.catalogItemVersion.displayText)}`}
            </Text>
            <Col>
              <Text color="neutral70" fontSize="T200">
                {getString("checkout.sidePanel.price")}
              </Text>
              <Text color="neutral90" fontSize="T200" fontWeight="bold">
                {`${show(checkoutValues.catalogItemVersion.currency)} $${show(
                  checkoutValues.catalogItemVersion.amount.toString()
                )}`}
              </Text>
            </Col>
            <Col>
              <Text color="neutral70" fontSize="T200">
                {getString("checkout.sidePanel.version")}
              </Text>
              <Text color="neutral90" fontSize="T200" fontWeight="bold">
                {show(checkoutValues.catalogItemVersion.displayText)}
              </Text>
            </Col>
            <Row>
              {props.options.length > 0 && (
                <WithModal renderModal={renderChangeVersionModal} isScrollable={false}>
                  {({ open }) => (
                    <Button
                      data-testid="change-version-button"
                      size={ButtonSize.Small}
                      variant={ButtonVariant.Tertiary}
                      onClick={open}
                    >
                      {getString("checkout.sidePanel.changeVersion")}
                    </Button>
                  )}
                </WithModal>
              )}
            </Row>
          </Col>
        </Col>
      </Container>
    </Col>
  );
};

export default CheckoutItemSidePanel;
